import type { ComponentProps, ComponentType, ReactElement } from 'react';

import type { Contact } from '@/api/types/contacts';
import { Icon, type IconName } from '@/components/ui';

import { AchievementsSection } from './AchievementsSection';
import { AddressesSection } from './AddressesSection';
import { AttachmentsSection } from './AttachmentsSection';
import { CertificationsSection } from './CertificationsSection';
import { ConsentsSection } from './ConsentsSection';
import { ContactInfoSection } from './ContactInfoSection';
import { CzechFieldsSection } from './CzechFieldsSection';
import { EducationSection } from './EducationSection';
import { EmploymentSection } from './EmploymentSection';
import { EventsSection } from './EventsSection';
import { InteractionsSection } from './InteractionsSection';
import { JobApplicationsSection } from './JobApplicationsSection';
import { NotesSection } from './NotesSection';
import { ProfileSection } from './ProfileSection';
import { RatingSection } from './RatingSection';
import { ReferencesSection } from './ReferencesSection';
import { ServicesSection } from './ServicesSection';
import { SkillsSection } from './SkillsSection';
import { SocialProfilesSection } from './SocialProfilesSection';
import { TagsSection } from './TagsSection';

export type SectionFormProp = ComponentProps<typeof ProfileSection>['form'];
type SectionComponent = ComponentType<{ form: SectionFormProp }>;

export type SectionGroup = 'core' | 'professional' | 'engagement' | 'meta';

export interface SectionDef {
  key: string;
  label: string;
  /** Lazy renderer so each section's nav glyph stays a plain Icon, not a heavy MUI svg. */
  icon: () => ReactElement;
  Component: SectionComponent;
  isEmpty: (c: Contact | undefined) => boolean;
  required?: boolean;
  group: SectionGroup;
}

const icon = (name: IconName) => () => <Icon name={name} size={14} />;

export const sectionRegistry: SectionDef[] = [
  {
    key: 'profile',
    label: 'Profile',
    icon: icon('users'),
    Component: ProfileSection,
    isEmpty: () => false,
    required: true,
    group: 'core',
  },
  {
    key: 'contactInfo',
    label: 'Contact Info',
    icon: icon('mail'),
    Component: ContactInfoSection,
    isEmpty: (c) => {
      const ct = c?.contact;
      if (!ct) return true;
      const hasEmails = !!ct.primaryEmail || (ct.emails?.length ?? 0) > 0;
      const hasPhones = (ct.phones?.length ?? 0) > 0;
      return !hasEmails && !hasPhones;
    },
    required: true,
    group: 'core',
  },
  {
    key: 'socialProfiles',
    label: 'Social Profiles',
    icon: icon('link'),
    Component: SocialProfilesSection,
    isEmpty: (c) => (c?.contact?.socialProfiles?.length ?? 0) === 0,
    group: 'core',
  },
  {
    key: 'addresses',
    label: 'Addresses',
    icon: icon('mapPin'),
    Component: AddressesSection,
    isEmpty: (c) => (c?.contact?.addresses?.length ?? 0) === 0,
    group: 'core',
  },
  {
    key: 'employment',
    label: 'Employment History',
    icon: icon('note'),
    Component: EmploymentSection,
    isEmpty: (c) => (c?.employment?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'education',
    label: 'Education',
    icon: icon('note'),
    Component: EducationSection,
    isEmpty: (c) => (c?.education?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'skills',
    label: 'Skills',
    icon: icon('sparkles'),
    Component: SkillsSection,
    isEmpty: (c) => (c?.skills?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'certifications',
    label: 'Certifications & Licenses',
    icon: icon('shield'),
    Component: CertificationsSection,
    isEmpty: (c) => (c?.certifications?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'achievements',
    label: 'Achievements & Publications',
    icon: icon('star'),
    Component: AchievementsSection,
    isEmpty: (c) => (c?.achievements?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'references',
    label: 'References',
    icon: icon('users'),
    Component: ReferencesSection,
    isEmpty: (c) => (c?.references?.length ?? 0) === 0,
    group: 'professional',
  },
  {
    key: 'services',
    label: 'Services',
    icon: icon('tags'),
    Component: ServicesSection,
    isEmpty: (c) => (c?.services?.length ?? 0) === 0,
    group: 'engagement',
  },
  {
    key: 'eventsAttended',
    label: 'Events Attended',
    icon: icon('calendar'),
    Component: EventsSection,
    isEmpty: (c) => (c?.eventsAttended?.length ?? 0) === 0,
    group: 'engagement',
  },
  {
    key: 'interactions',
    label: 'Interactions',
    icon: icon('send'),
    Component: InteractionsSection,
    isEmpty: (c) => (c?.interactions?.length ?? 0) === 0,
    group: 'engagement',
  },
  {
    key: 'jobApplications',
    label: 'Job Applications',
    icon: icon('send'),
    Component: JobApplicationsSection,
    isEmpty: (c) => (c?.jobApplications?.length ?? 0) === 0,
    group: 'engagement',
  },
  {
    key: 'notes',
    label: 'Notes',
    icon: icon('note'),
    Component: NotesSection,
    isEmpty: (c) => (c?.notes?.length ?? 0) === 0,
    group: 'engagement',
  },
  {
    key: 'attachments',
    label: 'Attachments',
    icon: icon('upload'),
    Component: AttachmentsSection,
    isEmpty: (c) => (c?.attachments?.length ?? 0) === 0,
    group: 'engagement',
  },
  // Note: StatusSection ("Status & preferences") is not registered here — it
  // renders permanently in the detail aside (ContactDetailSide), not as a
  // reorderable main-column section. Persisted layouts containing the old
  // 'status' key are tolerated (reconcileOrder drops unknown keys).
  {
    key: 'tags',
    label: 'Tags',
    icon: icon('tags'),
    Component: TagsSection,
    isEmpty: (c) => (c?.tags?.length ?? 0) === 0,
    group: 'meta',
  },
  {
    key: 'consents',
    label: 'Consents',
    icon: icon('shield'),
    Component: ConsentsSection,
    isEmpty: (c) => {
      const cs = c?.consents;
      if (!cs) return true;
      return !cs.marketing?.value && !cs.analytics?.value && !cs.thirdParty?.value;
    },
    group: 'meta',
  },
  {
    key: 'rating',
    label: 'Rating',
    icon: icon('star'),
    Component: RatingSection,
    isEmpty: (c) => {
      const r = c?.rating;
      if (!r) return true;
      return r.score === undefined && !r.engagement;
    },
    group: 'meta',
  },
  {
    key: 'czechFields',
    label: 'Czech-Specific Fields',
    icon: icon('globe'),
    Component: CzechFieldsSection,
    isEmpty: (c) => {
      const cz = c?.czechFields;
      if (!cz) return true;
      return !cz.vokativ && !cz.vokativSurname && !cz.nameday;
    },
    group: 'meta',
  },
];

export const sectionRegistryByKey: Record<string, SectionDef> = Object.fromEntries(
  sectionRegistry.map((s) => [s.key, s]),
);
